import type { MutableRefObject } from "react";
import { useEffect, useRef } from "react";
import type { PermissionContextDto } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { resolveActiveAccessTokenFromSession } from "@Front/Api/helpers/authTokenResolutionHelpers";
import { UserStore } from "@Front/Stores/UserStore";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { ThirdPartyStore } from "@Front/Stores/ThirdPartyStore";
import { getActiveOfficeStore } from "@Front/Stores/ActiveOfficeStore";
import { getCustomerOfficeStore } from "@Front/Stores/CustomerOfficeStore";
import type { Matrix } from "./useRolePermissionsRefreshExecutor";

function subscribeSessionPermissionListeners(
	refreshRef: MutableRefObject<() => Promise<void>>,
	setMatrix: (value: Matrix) => void,
	setPermissionContext: (value: PermissionContextDto | null) => void,
): () => void {
	const handleConnect = (): void => {
		const accessTokenCookie = resolveActiveAccessTokenFromSession();
		if (accessTokenCookie) {
			getCustomerOfficeStore().reset();
			queueMicrotask(() => {
				void refreshRef.current();
			});
		}
	};

	const handleDisconnect = (): void => {
		queueMicrotask(() => {
			setMatrix(null);
			setPermissionContext(null);
			getActiveOfficeStore().syncActiveOfficeFromContext(null);
			getCustomerOfficeStore().reset();
		});
	};

	const unsubscribeUserConnect = UserStore.instance.onConnect(handleConnect);
	const unsubscribeUserDisconnect = UserStore.instance.onDisconnect(handleDisconnect);
	const unsubscribeCustomerConnect = CustomerStore.instance.onConnect(handleConnect);
	const unsubscribeCustomerDisconnect = CustomerStore.instance.onDisconnect(handleDisconnect);
	const unsubscribeThirdPartyConnect = ThirdPartyStore.instance.onConnect(handleConnect);
	const unsubscribeThirdPartyDisconnect = ThirdPartyStore.instance.onDisconnect(handleDisconnect);

	return (): void => {
		unsubscribeUserConnect();
		unsubscribeUserDisconnect();
		unsubscribeCustomerConnect();
		unsubscribeCustomerDisconnect();
		unsubscribeThirdPartyConnect();
		unsubscribeThirdPartyDisconnect();
	};
}

export function useRolePermissionsEventListeners(
	refresh: () => Promise<void>,
	setMatrix: (value: Matrix) => void,
	setPermissionContext: (value: PermissionContextDto | null) => void,
	setLoading: (value: boolean) => void,
): void {
	const refreshRef = useRef(refresh);
	useEffect(() => {
		refreshRef.current = refresh;
	}, [refresh]);

	useEffect(() => {
		const accessTokenCookie = resolveActiveAccessTokenFromSession();
		if (accessTokenCookie) {
			void refreshRef.current();
		} else {
			setLoading(false);
		}
	}, [setLoading]);

	useEffect(() => {
		return subscribeSessionPermissionListeners(refreshRef, setMatrix, setPermissionContext);
	}, [setMatrix, setPermissionContext]);
}
